// File: CheckoutPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useOrder } from '../Context/OrderContext';
import '../Assets/Styles/CheckoutPage.css';
import { useNavigate } from 'react-router-dom';
import api from '../Api/Axios';
import Joi from 'joi';
import { PriceListsMap } from '../Utils/PriceLists'
import { fetchPricesForProducts } from '../Api/PriceList'

function CheckoutPage() {
    const {
        selectedClient,
        selectedProducts,
        setSelectedProducts,
        actionType,
        setActionType,
        updateProduct,
        removeProduct,
        resetOrder,
        useTarifPrices,
        setUseTarifPrices,
        selectedPriceListId,
        setSelectedPriceListId,
    } = useOrder();

    const navigate = useNavigate();

    // === 🗓️ Sell date setup ===
    const getTodayDateMMDDYYYY = () => {
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy = today.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const [sellDateInput, setSellDateInput] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [showDiscountInputs, setShowDiscountInputs] = useState(false);
    const prevProductIdsRef = useRef([]);


    const isValidDate = (dateStr) => {
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return false;
        const [dd, mm, yyyy] = dateStr.split('/').map(Number);
        const date = new Date(yyyy, mm - 1, dd);
        return (
            date.getFullYear() === yyyy &&
            date.getMonth() === mm - 1 &&
            date.getDate() === dd
        );
    };

    const formatSellDateForPayload = (input) => {
        const parts = input.split('/');
        if (parts.length !== 3) return null;
        const [dd, mm, yyyy] = parts;
        return `${yyyy.padStart(4, '0')}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    };

    const normalizeNumber = (val) => {
        if (typeof val === 'string') {
            return parseFloat(val.replace(',', '.'));
        }
        return parseFloat(val);
    };

    const handleInputChange = (id, field, value) => {
        const cleanValue = typeof value === 'string' ? value.replace(',', '.') : value;
        if (!/^(\d+)?([.]?\d*)?$/.test(cleanValue)) return;
        if (field === 'quantity' && parseFloat(cleanValue) < 1) return;
        updateProduct(id, field, cleanValue);
    };


    const calculateTotal = (priceNet, quantity, taxRate) => {
        const net = normalizeNumber(priceNet || 0);
        const qty = normalizeNumber(quantity || 0);
        const tax = normalizeNumber(taxRate || 0);
        return (net * (1 + tax / 100) * qty).toFixed(2);
    };

    const grandTotal = selectedProducts
        .reduce((sum, p) => sum + parseFloat(calculateTotal(p.price_net, p.quantity, p.tax)), 0)
        .toFixed(2);


    const fetchTarifPricesAndUpdateProducts = async (priceListId) => {
        try {
            const productIds = selectedProducts.map(p => p.id);

            const pricesMap = await fetchPricesForProducts(priceListId, productIds);

            const updated = selectedProducts.map(p => {
                const priceData = pricesMap[p.id];
                if (priceData?.price_net) {
                    return {
                        ...p,
                        price_net: parseFloat(priceData.price_net),
                    };
                }
                return p;
            });

            setSelectedProducts(updated);
        } catch (err) {
            console.error('❌ Failed to fetch tarif prices:', err);
            alert('❌ Could not fetch prices: ' + (err.response?.data?.error || err.message));
        }
    };

    useEffect(() => {
        if (!useTarifPrices || !selectedPriceListId) return;

        const currentIds = selectedProducts.map(p => p.id);
        const prevIds = prevProductIdsRef.current;

        // Find newly added product IDs
        const newlyAdded = currentIds.filter(id => !prevIds.includes(id));

        if (newlyAdded.length > 0) {
            // Fetch prices only for new items
            fetchPricesForProducts(selectedPriceListId, newlyAdded)
                .then((pricesMap) => {
                    const updated = selectedProducts.map(p => {
                        if (newlyAdded.includes(p.id) && pricesMap[p.id]?.price_net) {
                            return {
                                ...p,
                                price_net: parseFloat(pricesMap[p.id].price_net),
                            };
                        }
                        return p;
                    });
                    setSelectedProducts(updated);
                })
                .catch(err => {
                    console.error('❌ Failed to fetch price for new product(s):', err);
                });
        }

        // Update the ref for next time
        prevProductIdsRef.current = currentIds;
    }, [selectedProducts, useTarifPrices, selectedPriceListId]);

    // Submit Invoice/BDC
    const handleSubmit = async () => {
        if (!selectedClient || !selectedProducts.length || !actionType) {
            alert('Please select a client, products, and action type.');
            return;
        }

        const effectiveSellDate = sellDateInput.trim() === ''
            ? getTodayDateMMDDYYYY()
            : sellDateInput;

        if (!isValidDate(effectiveSellDate)) {
            alert('Please enter a valid sell date in DD/MM/YYYY format.');
            return;
        }

        const payload = {
            client_id: selectedClient.id,
            invoiceKind: actionType,
            sell_date: formatSellDateForPayload(effectiveSellDate),
            ...(invoiceNumber && { oid: invoiceNumber }),
            ...(showDiscountInputs && {
                discount_kind: 'percent_unit_gross',
                show_discount: true,
            }),
            ...(useTarifPrices && selectedPriceListId && {
                use_prices_from_price_lists: true,
                price_list_id: selectedPriceListId,
            }),
            positions: selectedProducts.map((p) => {
                const priceNet = normalizeNumber(p.price_net || 0);
                const taxRate = normalizeNumber(p.tax || 0);
                const qty = normalizeNumber(p.quantity || 0);
                const priceGross = priceNet * (1 + taxRate / 100);
                const discountPercent = normalizeNumber(p.discount_percent || 0);

                return {
                    product_id: p.id,
                    quantity: parseFloat(qty.toFixed(3)),
                    tax: parseFloat(taxRate.toFixed(2)),
                    total_price_gross: parseFloat((priceGross * qty).toFixed(2)),
                    quantity_unit: p.quantity_unit || undefined,
                    ...(showDiscountInputs && discountPercent > 0 && {
                        discount_percent: parseFloat(discountPercent.toFixed(2)),
                    }),
                };
            }),
        };

        const schema = Joi.object({
            client_id: Joi.number().positive().required(),
            invoiceKind: Joi.string().valid('vat', 'client_order').required(),
            sell_date: Joi.string().pattern(/^\d{4}-\d{2}-\d{2}$/).required(),
            oid: Joi.string().alphanum().max(100).optional(),
            discount_kind: Joi.string().valid('percent_unit_gross').optional(),
            show_discount: Joi.boolean().optional(),
            use_prices_from_price_lists: Joi.boolean().optional(),
            price_list_id: Joi.number().positive().optional(),
            positions: Joi.array().items(
                Joi.object({
                    product_id: Joi.number().positive().required(),
                    quantity: Joi.number().positive().required(),
                    tax: Joi.number().min(0).max(100).required(),
                    total_price_gross: Joi.number().positive().required(),
                    quantity_unit: Joi.string().max(10).allow('', null).optional(),
                    discount_percent: Joi.number().min(0).max(100).optional()
                })
            ).min(1).required(),
        });


        const { error } = schema.validate(payload);
        if (error) {
            alert('❌ Validation error: ' + error.details.map(d => d.message).join(', '));
            return;
        }
        try {
            const response = await api.post('/invoices', payload);
            if (response.status === 201 || response.status === 200) {
                alert('✅ Invoice created successfully!');
                if (resetOrder) resetOrder();
                navigate('/clients');
            } else {
                console.warn('⚠️ Unexpected response:', response.status);
                alert('⚠️ Invoice created with an unexpected status.');
            }
        } catch (error) {
            console.error('❌ Submission failed:', error);
            alert('❌ Submission failed: ' + (error.response?.data?.error || error.message || 'Unexpected error.'));
        }
    };


    return (
        <div className="checkout-container">
            {selectedProducts.length > 0 ? (
                <div className="checkout-product-summary">
                    <div className='checkout-header'>
                        <h2>🛒 Selected Products</h2>
                        <div className='header-fields'>
                            {/* Sell Date Input */}
                            <div className='sell-date-field'>
                                <label htmlFor="sell-date-field">
                                    Sell Date
                                </label>
                                <input
                                    id="sell-date"
                                    type="text"
                                    placeholder={getTodayDateMMDDYYYY()}
                                    value={sellDateInput}
                                    onChange={(e) => setSellDateInput(e.target.value)}
                                />
                            </div>
                            {/* Ivoice number */}
                            <div className="invoice-number-field">
                                <label htmlFor="invoice-number">Invoice Number</label>
                                <input
                                    type="text"
                                    id="invoice-number"
                                    value={invoiceNumber}
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Desktop Table View */}
                    <table className="product-table desktop-only">
                        <thead>
                            <tr>
                                <th>Product Name</th>
                                <th>Qty</th>
                                <th>Tax %</th>
                                {showDiscountInputs && (<th>↓%</th>)}
                                <th>PU HT €</th>
                                <th>Total TTC €</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedProducts.map((product) => (
                                <tr key={product.id} className="checkout-product-row">
                                    <td className="name-cell">
                                        {product.name}
                                        <button className="remove-btn" onClick={() => removeProduct(product.id)}>✕</button>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={product.quantity}
                                            onChange={(e) => handleInputChange(product.id, 'quantity', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        {product.quantity_unit}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={typeof product.tax === 'string' ? product.tax.replace(',', '.') : product.tax}
                                            onChange={(e) => handleInputChange(product.id, 'tax', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                    </td>
                                    {showDiscountInputs && (
                                        <td>
                                            <input
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="100"
                                                value={product.discount_percent || ''}
                                                onChange={(e) => handleInputChange(product.id, 'discount_percent', e.target.value)}
                                                onFocus={(e) => e.target.select()}
                                            />
                                        </td>
                                    )}
                                    <td>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={product.price_net}
                                            onChange={(e) => handleInputChange(product.id, 'price_net', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                    </td>
                                    <td>{calculateTotal(product.price_net, product.quantity, product.tax)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Mobile List View */}
                    <div className="mobile-only checkout-product-list">
                        {selectedProducts.map((product) => (
                            <div key={product.id} className="checkout-product-card">
                                <div className="checkout-product-header">
                                    <strong>{product.name}</strong>
                                    <button className="remove-btn" onClick={() => removeProduct(product.id)}>✕</button>
                                </div>
                                <div className="checkout-product-field">
                                    <div>
                                        <input
                                            name='product-quantity'
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            min="0"
                                            value={product.quantity}
                                            onChange={(e) => handleInputChange(product.id, 'quantity', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        {product.quantity_unit}
                                    </div>
                                    <div>
                                        <input
                                            name='product-tax'
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            min="0"
                                            value={product.tax}
                                            onChange={(e) => handleInputChange(product.id, 'tax', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        <strong>Tax </strong>
                                    </div>
                                    {showDiscountInputs && (
                                        <div>
                                            <input
                                                name='product-discount'
                                                type="number"
                                                inputMode="decimal"
                                                step="1"
                                                min="0"
                                                max="100"
                                                value={product.discount_percent || ''}
                                                onChange={(e) => handleInputChange(product.id, 'discount_percent', e.target.value)}
                                                onFocus={(e) => e.target.select()}
                                            />
                                            <strong>↓%</strong>
                                        </div>
                                    )}
                                    <div>
                                        <input
                                            name='product-price'
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            min="0"
                                            value={product.price_net}
                                            onChange={(e) => handleInputChange(product.id, 'price_net', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        <strong>PU </strong>
                                    </div>
                                    <div>
                                        <strong>TTC:</strong> {calculateTotal(product.price_net, product.quantity, product.tax)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Checkout Control Fields */}
                    <div className='checkout-control-fields'>
                        {/* Toggle Discount Button */}
                        <div className='toggle-discount'>
                            <button
                                onClick={() => setShowDiscountInputs(prev => !prev)}
                            >
                                {showDiscountInputs ? '➖ Hide Discount' : '➕ Add Discount'}
                            </button>
                        </div>


                        {/* Tarif Selection Section */}
                        <div className="tarif-selection">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={useTarifPrices}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setUseTarifPrices(checked);

                                        if (!checked) {
                                            setSelectedPriceListId(null); // Clear selection if unchecked
                                        }
                                    }}
                                />
                                Use Tarif Price List
                            </label>

                            {useTarifPrices && (
                                <select
                                    value={selectedPriceListId || ''}
                                    onChange={async (e) => {
                                        const priceListId = Number(e.target.value);
                                        setSelectedPriceListId(priceListId);

                                        if (priceListId && selectedProducts.length > 0) {
                                            await fetchTarifPricesAndUpdateProducts(priceListId);
                                        }
                                    }}
                                >
                                    <option value="" disabled>Select a Tarif</option>
                                    {PriceListsMap.map((pl) => (
                                        <option key={pl.id} value={pl.id}>
                                            {pl.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>


                        {/* Tax Free Section */}
                        <div className="checkbox-label">
                            <label>
                                <input
                                    type="checkbox"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const confirmed = window.confirm("Set tax to 0% for all selected products?");
                                            if (confirmed) {
                                                selectedProducts.forEach(product => {
                                                    handleInputChange(product.id, 'tax', 0);
                                                });
                                            } else {
                                                e.target.checked = false;
                                            }
                                        }
                                    }}
                                />
                                Set Tax to 0% for All
                            </label>
                        </div>

                        <div className="total-section">
                            <h4>🧮 Total: {grandTotal} €</h4>
                        </div>
                    </div>

                    <button
                        className="reset-order"
                        onClick={() => {
                            const confirmed = window.confirm("Are you sure you want to reset the order?");
                            if (confirmed) {
                                resetOrder();
                            }
                        }}
                    >
                        Reset Order
                    </button>
                </div>
            ) : (
                <p>No products selected.</p>
            )}

            {selectedClient ? (
                <div className="action-select">
                    <h3>
                        What do you want to create for{' '}
                        <span className="client-highlight">{selectedClient.name}</span>?
                    </h3>

                    <div className="radio-group">
                        <label>
                            <input
                                type="radio"
                                name="actionType"
                                value="vat"
                                checked={actionType === 'vat'}
                                onChange={() => setActionType('vat')}
                            />
                            🧾 Invoice
                        </label>

                        <label>
                            <input
                                type="radio"
                                name="actionType"
                                value="client_order"
                                checked={actionType === 'client_order'}
                                onChange={() => setActionType('client_order')}
                            />
                            📄 Bon De Commande
                        </label>
                    </div>
                </div>
            ) : (
                <button className='searchClient-btn' onClick={() => navigate('/clients')}>
                    🔍 Select a Client
                </button>
            )}

            {selectedProducts.length > 0 && selectedClient && actionType && (
                <div className="submit-section">
                    <button className="submit-btn" onClick={handleSubmit}>
                        ✅ Confirm & Submit
                    </button>
                </div>
            )}
        </div>
    );
}

export default CheckoutPage;
